import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

/**
 * AdminTaxMigration.jsx
 * --------------------------------------------------------------
 * "Change tax category A → B by (partial) storno, then issue B-sale"
 */

// --- API roots (mirrors used in UserPOSDashboard)
const API = process.env.REACT_APP_API_BASE || ""; // cookie / proxy base
const API_BASE =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE || "";

async function apiJson(url, opts = {}) {
  const token = localStorage.getItem("token") || "";
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    ...opts,
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : null;
  if (!res.ok) {
    const msg = data?.device_error
      ? `${data.device_error}${data?.last_command ? ` (cmd: ${data.last_command})` : ""}`
      : data?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

// --- Small normalizers ---------------------
const normDocDT = (val) => {
  const s = String(val || "");
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : s;
};
const toDocNo = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  if (!s || /^0+$/.test(s)) return null;
  return String(parseInt(s, 10)).slice(0, 8);
};
const toFM = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  return s ? s.padStart(8, "0") : null;
};
const toSerial = (v) => {
  const m = String(v || "").toUpperCase().match(/\bDY\d{6}\b/);
  return m ? m[0] : null;
};
const toDocDT_YY = (v) => {
  const s = normDocDT(v);
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1].slice(-2)} ${m[4]}:${m[5]}:${m[6] || "00"}`;
};

// --- Tax group pretty printing (1..8 → А..З) -----------------
const taxLetterFromNumber = (code) => {
  const n = Number(code);
  if (!Number.isFinite(n) || n < 1) return null;
  const letters = "АБВГДЕЖЗ";
  return letters[n - 1] || null;
};
const prettyTaxGroup = (code) => taxLetterFromNumber(code) || String(code ?? "?");
const prettyTaxGroupList = (codes) => (codes || []).map(prettyTaxGroup).join(",");

// --- Helpers
const pmBucket = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "cash";
  if (/(card|pos|terminal|bank[_\s-]?card|debit|credit)/.test(s)) return "card";
  if (/(cash|кеш)/.test(s)) return "cash";
  return s;
};

export default function AdminTaxMigration() {
  // ===== Auth / Cinema =====
  const [cinemaId, setCinemaId] = useState(null);
  const [cinemaName, setCinemaName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const payload = JSON.parse(atob((token.split(".")[1] || "").trim()));
      if (payload?.cinema_id != null) setCinemaId(Number(payload.cinema_id));
      const nameFromToken =
        payload?.cinema_name ||
        payload?.theatre_name ||
        payload?.company_name ||
        payload?.name ||
        null;
      if (nameFromToken) setCinemaName(String(nameFromToken));
    } catch {}
  }, []);

  // ===== Filters / Options =====
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(7, "day"));
  const [dateTo, setDateTo] = useState(dayjs());
  const [limit, setLimit] = useState(300);
  const [query, setQuery] = useState("");
  const [includeStorno, setIncludeStorno] = useState(false);
  const [consolidate, setConsolidate] = useState(true); // one big B receipt
  const [newPaymentMethod, setNewPaymentMethod] = useState("cash");

  // taxes
  const [taxes, setTaxes] = useState([]); // [{code,name,rate}]
  const [fromCodes, setFromCodes] = useState([]);       // now numeric codes as strings, e.g. ["1","2"]
  const [toCode, setToCode] = useState("");

  // data
  const [rows, setRows] = useState([]); // device rows (recent bons)
  const [busyLoad, setBusyLoad] = useState(false);
  const [busyRun, setBusyRun] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  // selection + cache of bon details
  const [checked, setChecked] = useState(new Set());
  const detailsRef = useRef(new Map()); // unp -> full bon with items

  // cache: tax codes per bon (from backend tax_codes column)
  // key: bon_number / unp, value: { codes: ["1","2"], matchesFrom: bool }
  const [bonTaxSummary, setBonTaxSummary] = useState({});

  const selectedCount = checked.size;

  // ===== Load taxes =====
  useEffect(() => {
    if (!cinemaId) return;
    (async () => {
      try {
        const { taxes: t, default_tax_code } = await apiJson(
          `${API_BASE}/api/fiscal/taxes?cinema_id=${cinemaId}`
        );
        setTaxes(Array.isArray(t) ? t : []);
        if (!toCode && default_tax_code != null) {
          setToCode(String(default_tax_code));
        }
      } catch {
        setTaxes([]);
      }
    })();
  }, [cinemaId, toCode]);

  // ===== Load recent device bons =====
  const loadRecent = useCallback(async () => {
    if (!cinemaId) return;
    setBusyLoad(true);
    try {
      const dev = await apiJson(
        `${API_BASE}/api/fiscal/recent-bons?cinema_id=${cinemaId}&limit=${Math.max(
          50,
          limit
        )}&include_negative=1`
      );
      let list = Array.isArray(dev?.bons) ? dev.bons : Array.isArray(dev) ? dev : [];

      // filter by date bounds (client side; device returns latest N)
      const df = dateFrom?.isValid?.() ? dateFrom.startOf("day") : null;
      const dt = dateTo?.isValid?.() ? dateTo.endOf("day") : null;
      list = list.filter((r) => {
        const s = normDocDT(r?.device_doc_datetime || r?.sale_time || r?.bon_datetime || "");
        const d = s ? dayjs(s.replace(" ", "T")) : null;
        if (df && d && d.isBefore(df)) return false;
        if (dt && d && d.isAfter(dt)) return false;
        return true;
      });

      // no negative unless explicitly included
      if (!includeStorno) list = list.filter((r) => Number(r.total_amount || 0) >= 0);

      // text search over a few fields
      const q = query.trim().toLowerCase();
      if (q) {
        list = list.filter((r) => {
          const hay = [
            r?.bon_number || r?.unp_code || "",
            r?.device_doc_no || "",
            r?.payment_method || "",
            String(r?.total_amount ?? ""),
            normDocDT(r?.device_doc_datetime || r?.sale_time || r?.bon_datetime || ""),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return q.split(/\s+/).every((t) => hay.includes(t));
        });
      }

      // newest first by doc datetime
      list.sort((a, b) =>
        String(
          normDocDT(b?.device_doc_datetime || b?.sale_time || b?.bon_datetime || "")
        ).localeCompare(
          String(
            normDocDT(a?.device_doc_datetime || a?.sale_time || a?.bon_datetime || "")
          )
        )
      );

      // build tax summary from backend tax_codes column
      const summary = {};
      for (const r of list) {
        const unp = r?.bon_number || r?.unp_code || "";
        const raw = String(r?.tax_codes || "");
        const codes = raw
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        if (!unp) continue;
        summary[unp] = { codes, matchesFrom: false };
      }

      setRows(list);
      setChecked(new Set());
      detailsRef.current.clear();
      setBonTaxSummary(summary);
    } catch (e) {
      setRows([]);
      setSnack({
        open: true,
        severity: "error",
        message: `Неуспех при зареждане: ${e.message}`,
      });
    } finally {
      setBusyLoad(false);
    }
  }, [cinemaId, limit, dateFrom, dateTo, includeStorno, query]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // when FROM group changes, recompute which bons "match" it (по данъчни групи)
  useEffect(() => {
    setBonTaxSummary((prev) => {
      const next = {};
      const fromSet = new Set(fromCodes.map(String));
      for (const [unp, info] of Object.entries(prev)) {
        const codes = info.codes || [];
        const matchesFrom =
          fromSet.size > 0
            ? codes.some((c) => fromSet.has(String(c)))
            : false;
        next[unp] = { ...info, matchesFrom };
      }
      return next;
    });
  }, [fromCodes]);

  // ===== Fetch bon details (with items) =====
  const fetchDetails = useCallback(
    async (unp) => {
      if (!unp) return null;
      if (detailsRef.current.get(unp)) return detailsRef.current.get(unp);
      const full = await apiJson(
        `${API_BASE}/api/fiscal/bon/${encodeURIComponent(unp)}?cinema_id=${cinemaId}`
      );
      detailsRef.current.set(unp, full);

      // на база на item-ите можем да прецизираме tax codes (ако трябва)
      try {
        const items = Array.isArray(full?.items) ? full.items : [];
        const codesSet = new Set();
        for (const it of items) {
          const c = String(
            it.tax_code ?? it.tax_dept ?? it.vat_dept ?? ""
          ).trim();
          if (c) codesSet.add(c);
        }
        setBonTaxSummary((prev) => {
          const existing = prev[unp] || { codes: [], matchesFrom: false };
          const codes = [...new Set([...existing.codes, ...codesSet])];
          const fromSet = new Set(fromCodes.map(String));
          const matchesFrom =
            fromSet.size > 0
              ? codes.some((c) => fromSet.has(String(c)))
              : existing.matchesFrom;
          return { ...prev, [unp]: { codes, matchesFrom } };
        });
      } catch {}

      return full;
    },
    [cinemaId, fromCodes]
  );

  // ===== Helper: extract tax code name/label
  const taxLabelFor = useCallback(
    (code) => {
      const t = taxes.find((x) => String(x.code) === String(code));
      const letter = prettyTaxGroup(code);
      if (!t) return `Гр. ${letter}`;
      const pct = Number(t.rate);
      const ratePart = Number.isFinite(pct) ? ` • ${pct}%` : "";
      return `Гр. ${letter}${ratePart}`;
    },
    [taxes]
  );

  const taxesOptions = useMemo(
    () =>
      (taxes || []).map((t) => ({
        label: `${taxLabelFor(t.code)}`,
        value: String(t.code),
      })),
    [taxes, taxLabelFor]
  );

  // ===== Scan selected bons → qualify FROM matches =====
  const [qualifying, setQualifying] = useState(false);
  const [qualReport, setQualReport] = useState([]); // [{unp,total,affects,sumA,partial,payment_method}]

  const scanSelected = useCallback(async () => {
    const picks = [...checked];
    if (!picks.length) {
      setSnack({ open: true, severity: "info", message: "Няма избрани бонове." });
      return;
    }
    if (!fromCodes.length) {
      setSnack({
        open: true,
        severity: "info",
        message: "Моля, избери FROM данъчна група.",
      });
      return;
    }
    setQualifying(true);
    try {
      const report = [];
      const fromSet = new Set(fromCodes.map(String));

      for (const unp of picks) {
        const info = await fetchDetails(unp);
        const items = Array.isArray(info?.items) ? info.items : [];

        const mapCode = (it) =>
          String(it.tax_code ?? it.tax_dept ?? it.vat_dept ?? "");
        const linesFrom = items.filter(
          (it) => fromSet.has(mapCode(it)) && Number(it.qty || 0) > 0
        );
        const sumFrom = linesFrom.reduce(
          (s, it) => s + Number(it.unit_price || 0) * Number(it.qty || 0),
          0
        );
        if (!linesFrom.length) continue; // skip receipts without FROM lines
        const total = Number(info.total_amount || 0);
        const partial = linesFrom.length < items.length;

        // обновяваме и cache за групите
        const codesSet = new Set(
          items
            .map(mapCode)
            .filter(Boolean)
            .map(String)
        );
        setBonTaxSummary((prev) => ({
          ...prev,
          [unp]: {
            codes: [...codesSet],
            matchesFrom: true,
          },
        }));

        report.push({
          unp: info.bon_number || info.unp || unp,
          total,
          affects: linesFrom.length,
          sumA: sumFrom,
          partial,
          payment_method: pmBucket(info.payment_method),
          doc_link: {
            docNo: toDocNo(info?.device_doc_no ?? info?.device_doc_number),
            docDateTime: toDocDT_YY(info?.device_doc_datetime || info?.bon_datetime),
            fmNo: toFM(info?.device_fm_no ?? info?.fm_no),
            deviceSerial: toSerial(info?.device_serial ?? info?.deviceSerial),
          },
        });
      }
      setQualReport(report);
      if (!report.length)
        setSnack({
          open: true,
          severity: "info",
          message: "Няма бонове с избраната данъчна група.",
        });
    } catch (e) {
      setSnack({
        open: true,
        severity: "error",
        message: `Сканирането се провали: ${e.message}`,
      });
    } finally {
      setQualifying(false);
    }
  }, [checked, fromCodes, fetchDetails]);

  const listQualSummary = useMemo(() => {
    if (!qualReport.length) return null;
    const totalAffected = qualReport.reduce((s, r) => s + r.affects, 0);
    const sumFrom = qualReport.reduce((s, r) => s + r.sumA, 0);
    const partialCount = qualReport.filter((r) => r.partial).length;
    return { totalAffected, sumFrom, partialCount };
  }, [qualReport]);

  // ===== RUN MIGRATION =====
  const runMigration = useCallback(async () => {
    const picks = [...checked];
    if (!picks.length) {
      setSnack({ open: true, severity: "info", message: "Няма избрани бонове." });
      return;
    }
    if (!fromCodes.length) {
      setSnack({
        open: true,
        severity: "info",
        message: "Моля, избери FROM данъчна група.",
      });
      return;
    }
    if (!toCode) {
      setSnack({
        open: true,
        severity: "info",
        message: "Моля, избери TO данъчна група.",
      });
      return;
    }

    setBusyRun(true);
    const aggregateLines = []; // used only if consolidate === true
    const fromSet = new Set(fromCodes.map(String));

    try {
      for (const unp of picks) {
        const info = await fetchDetails(unp);
        const items = Array.isArray(info?.items) ? info.items : [];

        const mapCode = (it) =>
          String(it.tax_code ?? it.tax_dept ?? it.vat_dept ?? "");
        const linesFrom = items
          .filter((it) => fromSet.has(mapCode(it)) && Number(it.qty || 0) > 0)
          .map((it) => ({
            text: it.text,
            qty: Number(it.qty || 0),
            unit_price: Number(it.unit_price || 0),
            tax_code: it.tax_code ?? it.tax_dept ?? it.vat_dept,
          }));
        if (!linesFrom.length) continue;

        const docNo = toDocNo(info?.device_doc_no ?? info?.device_doc_number);
        const docDTyy = toDocDT_YY(info?.device_doc_datetime || info?.bon_datetime);
        const fm = toFM(info?.device_fm_no ?? info?.fm_no);
        const serial = toSerial(info?.device_serial ?? info?.deviceSerial);

        const fromLabel = prettyTaxGroupList(fromCodes);
        const toLabel = prettyTaxGroup(toCode);

        const payload = {
          cinema_id: Number(cinemaId),
          bon_number: info?.bon_number || unp,
          original_unp: info?.bon_number || unp,
          reason: `Корекция дан. група ${fromLabel} → ${toLabel}`,
          payment_method: pmBucket(info?.payment_method || newPaymentMethod),
          doc_link: { docNo, docDateTime: docDTyy, fmNo: fm, deviceSerial: serial },
        };

        if (linesFrom.length < items.length) {
          // partial storno
          payload.lines = linesFrom;
        }

        await apiJson(`${API_BASE}/api/pos/storno`, {
          method: "POST",
          body: JSON.stringify(payload),
        });

        // 2) New B-sale
        const linesB = linesFrom.map((l) => ({ ...l, tax_code: toCode }));
        if (consolidate) {
          aggregateLines.push(...linesB);
        } else {
          await apiJson(`${API_BASE}/api/fiscal/print-sale`, {
            method: "POST",
            body: JSON.stringify({
              cinema_id: Number(cinemaId),
              payment_method: newPaymentMethod || pmBucket(info?.payment_method),
              items: linesB,
              header_lines: [
                cinemaName || "Кино",
                `Корекция дан. група ${fromLabel} → ${toLabel}`,
                `По бон № ${info?.bon_number || unp}`,
              ],
            }),
          });
        }
      }

      if (consolidate && aggregateLines.length) {
        const keyOf = (x) => `${x.text}|${x.unit_price}|${toCode}`;
        const byKey = new Map();
        for (const l of aggregateLines) {
          const k = keyOf(l);
          const cur = byKey.get(k) || { ...l, tax_code: toCode, qty: 0 };
          cur.qty += Number(l.qty || 0);
          byKey.set(k, cur);
        }
        const merged = [...byKey.values()];

        const fromLabel = prettyTaxGroupList(fromCodes);
        const toLabel = prettyTaxGroup(toCode);

        await apiJson(`${API_BASE}/api/fiscal/print-sale`, {
          method: "POST",
          body: JSON.stringify({
            cinema_id: Number(cinemaId),
            payment_method: newPaymentMethod,
            items: merged,
            header_lines: [
              cinemaName || "Кино",
              `Сборна корекция дан. група ${fromLabel} → ${toLabel}`,
              `(${selectedCount} бона)`,
            ],
          }),
        });
      }

      setSnack({
        open: true,
        severity: "success",
        message: "Готово. Сторно + нови продажби отпечатани.",
      });
      setChecked(new Set());
      setQualReport([]);
      detailsRef.current.clear();
      loadRecent();
    } catch (e) {
      setSnack({
        open: true,
        severity: "error",
        message: `Грешка при корекция: ${e.message}`,
      });
    } finally {
      setBusyRun(false);
    }
  }, [
    checked,
    fetchDetails,
    cinemaId,
    cinemaName,
    fromCodes,
    toCode,
    newPaymentMethod,
    consolidate,
    loadRecent,
    selectedCount,
  ]);

  // ===== Render =====
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: 2,
          height: "100vh",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Корекция на данъчна група (A → B) с автоматично сторно
        </Typography>

        {/* Филтри / настройки */}
        <Paper sx={{ p: 1.25 }} variant="outlined">
          <Stack spacing={1}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="center"
            >
              <DatePicker
                label="От дата"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
              />
              <DatePicker
                label="До дата"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
              />
              <TextField
                size="small"
                label="Лимит"
                type="number"
                value={limit}
                onChange={(e) =>
                  setLimit(Math.max(50, Number(e.target.value) || 100))
                }
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Търсене (UNP, Док.№, текст)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeStorno}
                    onChange={(e) => setIncludeStorno(e.target.checked)}
                  />
                }
                label="Вкл. сторно"
              />
              <Button
                variant="outlined"
                onClick={loadRecent}
                disabled={busyLoad}
              >
                {busyLoad ? "Зареждане…" : "Обнови"}
              </Button>
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={taxesOptions}
                value={taxesOptions.filter((o) =>
                  fromCodes.map(String).includes(o.value)
                )}
                onChange={(_, val) => setFromCodes(val.map((v) => v.value))}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="FROM дан. група (един или повече)"
                    size="small"
                  />
                )}
                sx={{ minWidth: 260, flex: 1 }}
              />

              <Autocomplete
                size="small"
                options={taxesOptions}
                value={
                  taxesOptions.find((o) => o.value === String(toCode)) || null
                }
                onChange={(_, val) => setToCode(val ? val.value : "")}
                renderInput={(p) => (
                  <TextField {...p} label="TO дан. група" size="small" />
                )}
                sx={{ minWidth: 220 }}
              />

              <TextField
                size="small"
                select
                SelectProps={{ native: true }}
                label="Плащане за новия бон"
                value={newPaymentMethod}
                onChange={(e) => setNewPaymentMethod(e.target.value)}
                sx={{ width: 220 }}
              >
                <option value="cash">КЕШ</option>
                <option value="card">КАРТА</option>
              </TextField>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={consolidate}
                    onChange={(e) => setConsolidate(e.target.checked)}
                  />
                }
                label="Един сборен B-бон"
              />

              <Button
                variant="contained"
                onClick={scanSelected}
                disabled={!checked.size || qualifying}
              >
                {qualifying
                  ? "Сканирам…"
                  : `Провери селекцията (${checked.size})`}
              </Button>
            </Stack>

            {!!listQualSummary && (
              <Alert severity="info">
                Ще бъдат засегнати <b>{listQualSummary.totalAffected}</b> реда
                (сума ≈{" "}
                <b>{listQualSummary.sumFrom.toFixed(2)} лв</b>) · частични сторно:{" "}
                <b>{listQualSummary.partialCount}</b>
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                color="error"
                variant="contained"
                disabled={!checked.size || busyRun}
                onClick={runMigration}
              >
                {busyRun ? "Работи…" : `СТАРТИРАЙ (${checked.size})`}
              </Button>
              {!!checked.size && (
                <Button variant="text" onClick={() => setChecked(new Set())}>
                  Изчисти селекцията
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* LIST */}
        <Paper sx={{ p: 1, flex: 1, overflow: "auto" }} variant="outlined">
          {!rows.length && !busyLoad ? (
            <Typography sx={{ opacity: 0.7 }}>
              Няма данни за избрания период.
            </Typography>
          ) : (
            rows.map((r) => {
              const unp = r?.bon_number || r?.unp_code || "";
              const dt = normDocDT(
                r?.device_doc_datetime || r?.sale_time || r?.bon_datetime || ""
              );
              const total = Number(r?.total_amount || 0);
              const storno = total < 0;
              const sel = checked.has(unp);

              const taxInfo = bonTaxSummary[unp] || { codes: [], matchesFrom: false };
              const codes = taxInfo.codes || [];
              const matchesFrom = !!taxInfo.matchesFrom;

              return (
                <Paper
                  key={`${unp}-${r?.device_doc_no || "-"}`}
                  variant="outlined"
                  sx={{
                    p: 0.75,
                    mb: 0.5,
                    borderColor: sel
                      ? "#1976d2"
                      : matchesFrom
                      ? "#9c27b0"
                      : undefined,
                    bgcolor: sel
                      ? "#e3f2fd"
                      : matchesFrom
                      ? "#f3e5f5"
                      : undefined,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Checkbox
                      size="small"
                      checked={sel}
                      onChange={(e) => {
                        const n = new Set(checked);
                        if (e.target.checked) n.add(unp);
                        else n.delete(unp);
                        setChecked(n);
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        №{unp} • Док.{r?.device_doc_no ?? "—"} •{" "}
                        {String(r?.payment_method || "").toUpperCase()}
                        {storno && (
                          <Chip
                            size="small"
                            label="СТОРНО"
                            color="error"
                            sx={{ ml: 0.75 }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        {dt || "–"}
                      </Typography>

                      {/* данъчни групи за този бон */}
                      {codes.length > 0 && (
                        <Box
                          sx={{
                            mt: 0.3,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {codes.map((c) => (
                            <Chip
                              key={c}
                              size="small"
                              variant="outlined"
                              label={taxLabelFor(c)}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 800 }}>
                      {total.toFixed(2)} лв
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const full = await fetchDetails(unp);
                          const items = Array.isArray(full?.items)
                            ? full.items
                            : [];
                          const fromSet = new Set(fromCodes.map(String));
                          const mapCode = (it) =>
                            String(
                              it.tax_code ?? it.tax_dept ?? it.vat_dept ?? ""
                            );
                          const linesFrom = items.filter(
                            (it) =>
                              fromSet.has(mapCode(it)) &&
                              Number(it.qty || 0) > 0
                          );
                          const sumFrom = linesFrom.reduce(
                            (s, it) =>
                              s +
                              Number(it.unit_price || 0) *
                                Number(it.qty || 0),
                            0
                          );
                          setSnack({
                            open: true,
                            severity: linesFrom.length
                              ? "success"
                              : "warning",
                            message: linesFrom.length
                              ? `Отговаря: ${linesFrom.length} реда • сума ≈ ${sumFrom.toFixed(
                                  2
                                )} лв`
                              : "Няма редове с избраната дан. група.",
                          });
                        } catch (e) {
                          setSnack({
                            open: true,
                            severity: "error",
                            message: `Детайли неуспешни: ${e.message}`,
                          });
                        }
                      }}
                    >
                      Провери
                    </Button>
                  </Stack>
                </Paper>
              );
            })
          )}
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
